import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Analytics } from '@genoa/analytics'
import { usePhoneVerification } from '@genoa/middle-end'
import { GenerateDevLoginResponse } from '@genoa/state-management/lib/modules/api/developer/types'
import { Box } from '@chakra-ui/react'
import { getAuth, signInWithCustomToken } from 'firebase/auth'

import { useAuthState } from '../../../contexts'
import { useRxpEligibility } from '../../../contexts/RxpEligibilityContext'
import { useEmbed, useReduxAction } from '../../../hooks'
import { useEmbedPrefills } from '../../../hooks/prefills/use-embed-prefills'
import { useRxpExperimentSetup } from '../../../hooks/use-rxp-experiment-setup'
import { RootState } from '../../../modules'
import { useGenerateDevLoginMutation } from '../../../modules/flexApi'
import { useAssociateEmbedSubmissionMutation } from '../../../modules/flexApi'
import { setPhoneNumberAction } from '../../../modules/onboarding'
import { useNavigation } from '../../../navigation'
import {
  Config,
  logger,
  setUserSignupStatus,
  useAmplitude,
  useAnalytics,
  useEnhancedTracking,
  useIterable,
} from '../../../providers'
import { createPhoneVerifiedEvent } from '../../../providers/iterable/user-events'
import * as Routes from '../../../routing/constants'
import { DevAuth } from './DevAuth'
import { SuccessfulPin } from './SuccessfulPin'

export const DevAuthContainer = () => {
  const { navigateToLogIn } = useNavigation()
  const navigate = useNavigate()
  const setPhoneNumber = useReduxAction(setPhoneNumberAction)
  const phoneNumber = useSelector((state: RootState) => state.onboarding.phoneNumber)
  const [shouldResetAccount, setShouldResetAccount] = useState(false)
  const [generateDevLoginMutation, { isLoading }] = useGenerateDevLoginMutation()
  const { setProcessingLogin, uid } = useAuthState()
  const [, phoneVerification] = usePhoneVerification()
  const { refetchExperimentsForUser, consentManagement } = useAmplitude()
  const iterable = useIterable()
  const { trackPhoneVerified } = useEnhancedTracking()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [smsNotificationsAccepted, setSmsNotificationsAccepted] = useState(false)
  const [customerId, setCustomerId] = useState('')
  const analytics = useAnalytics()
  const embed = useEmbed()
  const isEmbed = embed.isEmbed
  const [associateEmbedSubmission] = useAssociateEmbedSubmissionMutation()
  const { handlePrefill } = useEmbedPrefills()
  const { redirectToRxp, checkRxpEligibility, isRxpEligibilityLoading, isGeneratingAuthToken } = useRxpExperimentSetup()
  const { setEligibility } = useRxpEligibility()

  useEffect(() => {
    if (Config.ENVIRONMENT === 'production') {
      navigateToLogIn()
    }
  }, [])

  const handleNext = async () => {
    const response: GenerateDevLoginResponse = await generateDevLoginMutation({
      phone_number: `+1${phoneNumber.extracted}`,
      reset_account: shouldResetAccount,
    }).unwrap()
    setProcessingLogin(true)
    const result = await signInWithCustomToken(getAuth(), response.data.token)
    await handleSuccessLogin(result.user.uid)
    setCustomerId(result.user.uid)
  }

  const handleSMSOptInNext = () => {
    if (smsNotificationsAccepted) {
      iterable.setSMSOptInStatus(smsNotificationsAccepted)
    }
  }

  const handleRegisterSigninPinCodeNext = (customerId: string) => {
    try {
      return phoneVerification({ customer_public_id: customerId, phone_number: `1${phoneNumber.extracted}` })
    } catch (error: any) {
      logger.warn('DevAuthContainer', `Error during phone verification request: ${error?.message}`)
    }
  }

  const handleAssociateEmbedSubmission = (customerId: string) => {
    try {
      return associateEmbedSubmission({ customerId, client: embed.client, token: embed.token })
    } catch (error: any) {
      logger.warn('ConfirmPINContainer', `Error associating embed submission: ${error?.message}`)
    }
  }

  const handleSuccessLogin = async (customerId: string) => {
    await Promise.all([refetchExperimentsForUser(customerId), handleRegisterSigninPinCodeNext(uid!)])

    iterable.addEvent(createPhoneVerifiedEvent())
    trackPhoneVerified(customerId, phoneNumber.extracted)
    setUserSignupStatus()
    setShowSuccessModal(true)
  }

  const handleSuccessfulPinCodeNext = async (skipOptIn?: boolean | undefined) => {
    analytics.logEvent(Analytics.Events.SIGNIN_SUCCESSFUL_PIN_NEXT_CLICKED, { phoneNumber })
    analytics.logEvent(Analytics.Events.SIGNUP_SMS_OPT_IN_CLICKED, {
      optedIn: smsNotificationsAccepted,
      devLogin: true,
    })
    try {
      // TODO: RXP-CLEANUP
      const rxpEligible = await checkRxpEligibility(uid!, shouldResetAccount, isEmbed)
      setEligibility(rxpEligible)
      // We handle redirect here if customer is treatment and change the app entry to RXP
      if (rxpEligible) {
        // Storing the SMS opt-in consent in the async storage to be used by the RXP app

        logger.log('ConfirmPINContainer', 'Redirecting user from to RXP from legacy')
        await redirectToRxp(customerId, smsNotificationsAccepted)
        // early exit to avoid any legacy logic from running
        return
      }
    } catch (error: any) {
      logger.error('ConfirmPinContainer', `authToken failed to generate: ${error?.message}`)
    }

    if (skipOptIn) {
      analytics.logEvent(Analytics.Events.SIGNUP_SMS_SKIP_OPT_IN_CLICKED)
    } else {
      handleSMSOptInNext()
    }
    if (isEmbed) {
      await handleAssociateEmbedSubmission(uid!)
      await handlePrefill({
        navigateToAddressSelection: () => navigate(Routes.Onboarding.ADDRESS),
      })
    }
    setShowSuccessModal(false)
    setProcessingLogin(false)
  }

  const handleSMSOptIn = () => {
    setSmsNotificationsAccepted(!smsNotificationsAccepted)
  }
  const handleCheckbox = () => {
    setShouldResetAccount(!shouldResetAccount)
  }

  return (
    <Box>
      <DevAuth
        phone={phoneNumber.extracted}
        onPhoneChange={setPhoneNumber}
        onNextClick={handleNext}
        loading={isLoading}
        shouldResetAccount={shouldResetAccount}
        onCheck={handleCheckbox}
      />
      <SuccessfulPin
        visible={showSuccessModal}
        phoneNumber={phoneNumber}
        onClose={handleSuccessfulPinCodeNext}
        smsNotificationsAccepted={smsNotificationsAccepted}
        onSMSOptIn={handleSMSOptIn}
        loading={isGeneratingAuthToken || isRxpEligibilityLoading}
      />
    </Box>
  )
}
